// Markdown contracts for freelance projects — single source of truth shared by
// web, API and MCP. Round-trip must be stable: serialize(parse(text)) preserves
// ids, status and body text (outer whitespace in bodies may be trimmed once).

export type QuestionStatus = 'open' | 'clarified';

export interface ProjectQuestion {
  id: string;
  status: QuestionStatus;
  title: string;
  notes: string;
}

export interface ProjectAttachmentMeta {
  id: string;
  filename: string;
  bytes: number;
  contentType: string;
  addedAt: string;
  note: string;
}

const QUESTIONS_HEADER = '# Preguntas al cliente';
const ATTACHMENTS_HEADER = '# Adjuntos';
const QUESTION_HEADING_RE = /^## (q_[0-9a-f]{8}) \[(open|clarified)\]\s*$/;
const PREGUNTA_RE = /^\*\*Pregunta:\*\*\s*(.*)$/;
const ACLARACION_RE = /^\*\*Aclaración:\*\*\s*$/;

/**
 * Trims only blank lines (whitespace-only lines) from the start and end of a
 * string, leaving internal indentation untouched.
 */
function trimOuterBlankLines(s: string): string {
  return s.replace(/^[ \t]*\n+/, '').replace(/\n+[ \t]*$/, '');
}

/**
 * Parses `questions.md` into structured questions. The format is normative: any
 * deviation (missing header, unknown `##` heading, misplaced fields) throws.
 */
export function parseQuestionsMd(text: string): ProjectQuestion[] {
  const lines = text.split('\n');
  let i = 0;
  while (i < lines.length && lines[i].trim() === '') i++;

  if (lines[i]?.trim() !== QUESTIONS_HEADER) {
    throw new Error(`questions.md must start with "${QUESTIONS_HEADER}"`);
  }
  i++;

  const questions: ProjectQuestion[] = [];

  while (i < lines.length) {
    while (i < lines.length && lines[i].trim() === '') i++;
    if (i >= lines.length) break;

    const line = lines[i];
    if (!line.startsWith('## ')) {
      throw new Error(`Unexpected content at line ${i + 1}: expected a question heading`);
    }

    const heading = line.match(QUESTION_HEADING_RE);
    if (!heading) {
      throw new Error(`Invalid question heading: "${line.trim()}"`);
    }

    const id = heading[1];
    const status = heading[2] as QuestionStatus;
    i++;

    while (i < lines.length && lines[i].trim() === '') i++;
    const pregunta = lines[i]?.match(PREGUNTA_RE);
    if (!pregunta) {
      throw new Error(`Question ${id} is missing "**Pregunta:**"`);
    }
    const title = pregunta[1].trim();
    i++;

    while (i < lines.length && lines[i].trim() === '') i++;
    if (!lines[i]?.match(ACLARACION_RE)) {
      throw new Error(`Question ${id} is missing "**Aclaración:**"`);
    }
    i++;

    const notesLines: string[] = [];
    while (i < lines.length) {
      if (/^## /.test(lines[i])) break;
      notesLines.push(lines[i]);
      i++;
    }

    questions.push({
      id,
      status,
      title,
      notes: trimOuterBlankLines(notesLines.join('\n')),
    });
  }

  return questions;
}

/**
 * Serializes questions into the canonical `questions.md` body. The empty
 * document is just the header line (the create-seed shape).
 */
export function serializeQuestionsMd(questions: ProjectQuestion[]): string {
  if (questions.length === 0) return `${QUESTIONS_HEADER}\n`;
  const sections = questions.map((q) =>
    [
      `## ${q.id} [${q.status}]`,
      '',
      `**Pregunta:** ${q.title}`,
      '',
      '**Aclaración:**',
      '',
      q.notes,
    ].join('\n'),
  );
  return `${QUESTIONS_HEADER}\n\n${sections.join('\n\n')}\n`;
}

/** Adds or replaces a question keyed by its id. */
export function upsertQuestion(
  questions: ProjectQuestion[],
  next: ProjectQuestion,
): ProjectQuestion[] {
  const idx = questions.findIndex((q) => q.id === next.id);
  if (idx === -1) return [...questions, next];
  const out = [...questions];
  out[idx] = next;
  return out;
}

/** Removes a question by id (never reused after delete). */
export function removeQuestion(questions: ProjectQuestion[], id: string): ProjectQuestion[] {
  return questions.filter((q) => q.id !== id);
}

/**
 * Extracts the vault diagram ids referenced by ` ```diagram <id> [page] ``` `
 * fences — the same fence shape `extractDiagramIds` (api/lib/embed-refs.ts)
 * understands. Kept duplicated here so the shared package has no api import.
 */
export function extractProjectDiagramIds(md: string): string[] {
  const ids = new Set<string>();
  for (const match of md.matchAll(/```diagram\s+([^\s`]+)/g)) {
    ids.add(match[1]);
  }
  return Array.from(ids);
}

/**
 * Typed error thrown when `attachments.md` deviates from the canonical format.
 * Callers map it to HTTP 422 `ATTACHMENTS_PARSE_ERROR` (same contract as the
 * strict questions parser).
 */
export class AttachmentsParseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AttachmentsParseError';
  }
}

const ATTACHMENTS_COLUMNS = ['id', 'filename', 'bytes', 'contentType', 'addedAt', 'note'];
const ATTACHMENTS_SEPARATOR_CELLS = ATTACHMENTS_COLUMNS.map(() => '---').join('|');

function escapeCell(text: string): string {
  return text.replace(/\\/g, '\\\\').replace(/\|/g, '\\|');
}

function unescapeCell(text: string): string {
  return text.replace(/\\([\\|])/g, '$1');
}

/** Splits a table row on unescaped pipes only. */
function splitCells(line: string): string[] {
  return line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split(/(?<!\\)\|/)
    .map((c) => unescapeCell(c.trim()));
}

/**
 * Parses the machine-written `attachments.md` GFM table into metadata rows.
 * The format is normative like `questions.md`: any deviation (missing header,
 * malformed separator, bad row shape, non-numeric bytes) throws an
 * `AttachmentsParseError`. The empty seed (`# Adjuntos` and nothing else)
 * parses to zero attachments.
 */
export function parseAttachmentsMd(text: string): ProjectAttachmentMeta[] {
  const lines = text.split('\n');
  let i = 0;
  while (i < lines.length && lines[i].trim() === '') i++;
  if (lines[i]?.trim() !== ATTACHMENTS_HEADER) {
    throw new AttachmentsParseError(`attachments.md must start with "${ATTACHMENTS_HEADER}"`);
  }
  i++;

  while (i < lines.length && lines[i].trim() === '') i++;
  if (i >= lines.length) return [];

  const headerCells = splitCells(lines[i]);
  if (headerCells.join('|') !== ATTACHMENTS_COLUMNS.join('|')) {
    throw new AttachmentsParseError(`Invalid attachments.md table header: "${lines[i].trim()}"`);
  }
  i++;

  if (splitCells(lines[i] ?? '').join('|') !== ATTACHMENTS_SEPARATOR_CELLS) {
    throw new AttachmentsParseError(`Invalid attachments.md separator row: "${lines[i] ?? ''}"`);
  }
  i++;

  const rows: ProjectAttachmentMeta[] = [];
  for (; i < lines.length; i++) {
    if (lines[i].trim() === '') continue;
    if (!lines[i].trimStart().startsWith('|')) {
      throw new AttachmentsParseError(`Unexpected content at line ${i + 1}: expected a table row`);
    }
    const cells = splitCells(lines[i]);
    if (cells.length !== 6) {
      throw new AttachmentsParseError(
        `Attachment row at line ${i + 1} has ${cells.length} cells, expected 6`,
      );
    }
    const [id, filename, bytesStr, contentType, addedAt, note] = cells;
    if (!id || !filename) {
      throw new AttachmentsParseError(`Attachment row at line ${i + 1} is missing id or filename`);
    }
    if (!/^\d+$/.test(bytesStr)) {
      throw new AttachmentsParseError(`Attachment row at line ${i + 1} has a non-numeric bytes`);
    }
    rows.push({
      id,
      filename,
      bytes: Number.parseInt(bytesStr, 10),
      contentType,
      addedAt,
      note,
    });
  }
  return rows;
}

/** Serializes attachment metadata into the canonical `attachments.md` table. */
export function serializeAttachmentsMd(attachments: ProjectAttachmentMeta[]): string {
  const headerRow = `| ${ATTACHMENTS_COLUMNS.join(' | ')} |`;
  const separator = '| --- | --- | --- | --- | --- | --- |';
  const rows = attachments.map((a) => {
    const cells = [a.id, a.filename, String(a.bytes), a.contentType, a.addedAt, a.note].map(
      escapeCell,
    );
    return `| ${cells.join(' | ')} |`;
  });
  return (
    [ATTACHMENTS_HEADER, '', headerRow, separator, ...rows].join('\n') + (rows.length ? '\n' : '')
  );
}
